import {
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import GroupIcon from '@material-ui/icons/Group';
import { useMemo } from 'react';

const useStyles = makeStyles(theme => ({
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
    width: '100%'
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap'
  },
  chip: {
    margin: 2
  },
  queueItem: {
    fontWeight: 'bold',
    backgroundColor: theme.palette.action.hover,
    display: 'flex',
    alignItems: 'center'
  },
  userItem: {
    paddingLeft: theme.spacing(4)
  },
  queueIcon: {
    marginRight: theme.spacing(1),
    fontSize: 18
  },
  userIcon: {
    marginRight: theme.spacing(1),
    fontSize: 16
  },
  queueChip: {
    fontWeight: 'bold'
  }
}));

const GroupedUserSelect = ({ 
  users = [], 
  value = [], 
  onChange, 
  error, 
  helperText, 
  disabled = false,
  required = false,
  label = "Atendentes"
}) => {
  const classes = useStyles();

  // Agrupar usuários por filas
  const groupedData = useMemo(() => {
    const queuesMap = new Map();
    const usersWithoutQueue = [];
    
    users.forEach(user => {
      if (user.queues && user.queues.length > 0) {
        user.queues.forEach(queue => {
          if (!queuesMap.has(queue.id)) {
            queuesMap.set(queue.id, {
              id: queue.id,
              name: queue.name,
              color: queue.color,
              users: []
            });
          }
          queuesMap.get(queue.id).users.push(user);
        });
      } else {
        // Usuário sem fila
        usersWithoutQueue.push(user);
      }
    });

    // Converter para array e ordenar por nome da fila
    const sortedQueues = Array.from(queuesMap.values()).sort((a, b) => 
      a.name.localeCompare(b.name)
    );

    // Adicionar grupo virtual "Sem departamento" ao final se houver usuários sem fila
    if (usersWithoutQueue.length > 0) {
      sortedQueues.push({
        id: 'no-queue',
        name: 'Sem departamento',
        color: '#999',
        users: usersWithoutQueue,
        isVirtual: true // Marcador para grupo virtual
      });
    }

    return sortedQueues;
  }, [users]);

  // Verificar se todos os usuários de uma fila estão selecionados
  const isQueueFullySelected = (queueId) => {
    const queue = groupedData.find(q => q.id === queueId);
    if (!queue) return false;
    return queue.users.every(user => value.includes(user.id));
  };

  // Verificar se alguns (mas não todos) usuários de uma fila estão selecionados
  const isQueuePartiallySelected = (queueId) => {
    const queue = groupedData.find(q => q.id === queueId);
    if (!queue) return false;
    const selectedCount = queue.users.filter(user => value.includes(user.id)).length;
    return selectedCount > 0 && selectedCount < queue.users.length;
  };

  // Toggle de toda uma fila
  const handleQueueToggle = (queueId) => {
    const queue = groupedData.find(q => q.id === queueId);
    if (!queue) return;

    const queueUserIds = queue.users.map(u => u.id);
    
    if (isQueueFullySelected(queueId)) {
      // Remover todos os usuários desta fila
      const newValue = value.filter(id => !queueUserIds.includes(id));
      onChange(newValue);
    } else {
      // Adicionar todos os usuários desta fila
      const newValue = [...new Set([...value, ...queueUserIds])];
      onChange(newValue);
    }
  };

  // Toggle de um usuário individual
  const handleUserToggle = (userId) => {
    if (value.includes(userId)) {
      onChange(value.filter(id => id !== userId));
    } else {
      onChange([...value, userId]);
    }
  };

  // Renderizar os valores selecionados como chips
  const renderValue = (selected) => {
    if (selected.length === 0) return <em>Selecione os atendentes</em>;

    // Agrupar usuários selecionados por fila
    const selectedByQueue = new Map();
    
    selected.forEach(userId => {
      const user = users.find(u => u.id === userId);
      if (user && user.queues) {
        user.queues.forEach(queue => {
          if (!selectedByQueue.has(queue.id)) {
            selectedByQueue.set(queue.id, {
              queue,
              users: []
            });
          }
          // Evitar duplicatas
          if (!selectedByQueue.get(queue.id).users.find(u => u.id === userId)) {
            selectedByQueue.get(queue.id).users.push(user);
          }
        });
      }
    });

    const chips = [];
    
    // Adicionar chips de filas completas
    selectedByQueue.forEach((data, queueId) => {
      const queue = groupedData.find(q => q.id === queueId);
      if (queue && isQueueFullySelected(queueId)) {
        chips.push(
          <Chip
            key={`queue-${queueId}`}
            label={`${queue.name} (${queue.users.length})`}
            className={`${classes.chip} ${classes.queueChip}`}
            style={{
              backgroundColor: queue.color,
              color: '#fff'
            }}
            size="small"
            icon={<GroupIcon style={{ color: '#fff' }} />}
          />
        );
      }
    });

    // Adicionar chips de usuários individuais (que não estão em filas completas)
    selected.forEach(userId => {
      const user = users.find(u => u.id === userId);
      if (user) {
        // Verificar se o usuário está em alguma fila completamente selecionada
        const isInCompleteQueue = user.queues && user.queues.some(queue => 
          isQueueFullySelected(queue.id)
        );
        
        if (!isInCompleteQueue) {
          chips.push(
            <Chip
              key={`user-${userId}`}
              label={user.name}
              className={classes.chip}
              size="small"
            />
          );
        }
      }
    });

    return (
      <div className={classes.chips}>
        {chips}
      </div>
    );
  };

  return (
    <FormControl
      variant="outlined"
      className={classes.formControl}
      error={error}
      required={required}
      disabled={disabled}
    >
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        value={value}
        label={label}
        disabled={disabled}
        renderValue={renderValue}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 400
            }
          },
          getContentAnchorEl: null,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left'
          }
        }}
      >
        {groupedData.map(queue => {
          const isFullySelected = isQueueFullySelected(queue.id);
          const isPartiallySelected = isQueuePartiallySelected(queue.id);

          return [
            // Item da fila (grupo) - grupo virtual não é selecionável
            queue.isVirtual ? (
              <ListSubheader
                key={`queue-${queue.id}`}
                className={classes.queueItem}
                style={{ color: queue.color }}
              >
                <GroupIcon className={classes.queueIcon} style={{ color: queue.color }} />
                {`${queue.name} (${queue.users.length})`}
              </ListSubheader>
            ) : (
              <MenuItem
                key={`queue-${queue.id}`}
                value={`queue-${queue.id}`}
                className={classes.queueItem}
                onClick={(e) => {
                  e.stopPropagation();
                  handleQueueToggle(queue.id);
                }}
              >
                <Checkbox
                  checked={isFullySelected}
                  indeterminate={isPartiallySelected}
                  style={{ color: queue.color }}
                />
                <GroupIcon className={classes.queueIcon} style={{ color: queue.color }} />
                <ListItemText 
                  primary={`${queue.name} (${queue.users.length})`}
                  primaryTypographyProps={{
                    style: { fontWeight: 'bold' }
                  }}
                />
              </MenuItem>
            ),
            // Usuários da fila
            ...queue.users.map(user => (
              <MenuItem
                key={`user-${user.id}`}
                value={user.id}
                className={classes.userItem}
                onClick={(e) => {
                  e.stopPropagation();
                  handleUserToggle(user.id);
                }}
              >
                <Checkbox
                  checked={value.includes(user.id)}
                />
                <ListItemText 
                  primary={user.name}
                />
              </MenuItem>
            ))
          ];
        })}
      </Select>
      {helperText && (
        <FormHelperText>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
};

export default GroupedUserSelect;
